import { useEffect, useState } from 'react';
import { DataStore } from '@aws-amplify/datastore';
import { useNetworkStatus } from './useNetworkStatus';

// Check if Amplify is configured
const isAmplifyConfigured = () => {
  try {
    // Check if DataStore is available
    if (typeof DataStore === 'undefined') {
      return false;
    }
    
    // For Amplify Gen 1, check if aws-exports exists (it's imported in amplify-config.ts)
    // We can't directly check the import, but we can check if DataStore.query is available
    // The real check happens when we try to query - if it fails, we handle it gracefully
    return true; // Always try - let DataStore handle the error if not configured
  } catch {
    return false;
  }
};

/**
 * Hook for using Amplify DataStore with automatic sync
 * Falls back to empty data if Amplify is not configured (local mode)
 */
export function useAmplifyData<T>(
  model: any,
  options?: {
    filter?: any;
    sort?: (a: T, b: T) => number;
    limit?: number;
  }
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isOnline = useNetworkStatus();
  const amplifyAvailable = isAmplifyConfigured();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 useAmplifyData: Fetching data for model:', model?.name || 'unknown');

      // Always try to query - DataStore will throw if not configured
      let query: Promise<T[] | undefined> = DataStore.query(model) as Promise<T[] | undefined>;

      if (options?.filter) {
        query = DataStore.query(model, options.filter) as Promise<T[] | undefined>;
      }

      const results = (await query) || [];
      console.log('✅ useAmplifyData: Query successful, got', results?.length ?? 0, 'items');
      
      let sortedResults = results as T[];

      if (options?.sort) {
        sortedResults = sortedResults.sort(options.sort);
      }

      if (options?.limit) {
        sortedResults = sortedResults.slice(0, options.limit);
      }

      setData(sortedResults);
    } catch (err: any) {
      // If DataStore is not initialized, treat as local mode
      if (err?.message?.includes('DataStore') || err?.message?.includes('not configured') || err?.message?.includes('not initialized')) {
        console.log('⚠️ useAmplifyData: DataStore not available, using local mode');
        setData([]);
        setError(null); // Don't show error in local mode
      } else {
        setError(err);
        console.error('❌ useAmplifyData: Error fetching data:', err);
        console.error('   Error message:', err?.message);
        console.error('   Error stack:', err?.stack);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!amplifyAvailable) {
      // In local mode, just set loading to false
      setLoading(false);
      return;
    }

    fetchData();

    // Subscribe to changes for real-time updates
    let subscription: any = null;
    try {
      subscription = DataStore.observe(model).subscribe({
        next: () => {
          fetchData();
        },
      });
    } catch (err) {
      console.log('Could not subscribe to DataStore (local mode)');
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [model, options?.filter, options?.sort, options?.limit, amplifyAvailable]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    isOnline,
  };
}

